import { useEffect, useRef, useState } from 'react'
import { CurecallTextareaWithVariable, type TTextareaVariable } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useVariables } from '../../../../_components/variables-context'
import { getMessageLength } from '../../messages/_components/sms.util'

type TChatbotMessageTextareaProps = {
  label: string
  value: string
  onChange: (value: string) => void
  isRequired?: boolean
  tooltipContent?: string
  errorMessage?: string
  maxLength?: number
}

// CurecallTextareaWithVariable is an uncontrolled contentEditable: it only reads `defaultValue` on mount
// (and ignores empty values afterwards). To follow external changes (form reset when switching node),
// we remount it whenever `value` differs from the last value it emitted.
export function ChatbotMessageTextarea({
  label,
  value,
  onChange,
  isRequired,
  tooltipContent,
  errorMessage,
  maxLength,
}: Readonly<TChatbotMessageTextareaProps>) {
  const { t } = useTranslation()
  const { variableStore, contentVariables, defaultContentVariable } = useVariables()

  const lastEmittedValueRef = useRef(value)
  const [initialValue, setInitialValue] = useState(value)
  const [remountKey, setRemountKey] = useState(0)
  const [messageLength, setMessageLength] = useState(() => getMessageLength(value))
  const [variableToShow, setVariableToShow] = useState<TTextareaVariable | null>(null)

  useEffect(() => {
    if (value === lastEmittedValueRef.current) return
    lastEmittedValueRef.current = value
    setInitialValue(value)
    setMessageLength(getMessageLength(value))
    setRemountKey((previousKey) => previousKey + 1)
  }, [value])

  const handleChange = (messageContent: string, messageWithAverageExample: string) => {
    // CurecallTextareaWithVariable leaves a stray <br> / line break when the user clears the field,
    // which would otherwise prevent the required-field validation.
    const isEmpty = messageContent.replace(/<br\s*\/?>/gi, '').trim() === ''
    const normalizedContent = isEmpty ? '' : messageContent
    const normalizedExample = isEmpty ? '' : messageWithAverageExample

    setMessageLength(getMessageLength(normalizedExample))

    lastEmittedValueRef.current = normalizedContent
    onChange(normalizedContent)
  }

  return (
    <CurecallTextareaWithVariable
      key={remountKey}
      variableStore={variableStore}
      contentDropdownVariable={{
        label: t('pages.configuration.messages.content-name'),
        items: contentVariables,
        defaultVariable: defaultContentVariable,
      }}
      onChange={handleChange}
      defaultValue={initialValue}
      maxLength={maxLength}
      messageLength={messageLength}
      isRequired={isRequired}
      label={label}
      tooltipContent={tooltipContent ?? t('pages.configuration.messages.to-add-a-variable-instructions')}
      tooltipPosition="right"
      variablesExplainationText={t('pages.configuration.messages.click-on-variable')}
      variableToShow={variableToShow}
      setVariableToShow={setVariableToShow}
      modalTitle={t('pages.configuration.messages.variable-information', { label: variableToShow?.label })}
      modalCloseButtonAriaLabel={t('pages.configuration.messages.variable-information', {
        label: variableToShow?.label,
      })}
      modalExplainationsText={t('pages.configuration.messages.variable-information-explainations', {
        label: variableToShow?.label,
      })}
      modalForExampleText={t('pages.configuration.messages.for-example')}
      modalWillGiveText={t('pages.configuration.messages.for-example')}
      errorMessage={errorMessage}
      showVariableListOnFocusOnly
    />
  )
}
